import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import {
  DetailItem,
  SectionHeader,
  SectionCard,
  RespondNotesEditor,
} from '../../../../../../../shared/offer-domain/ui';
import CounterNegotiationCard from '../../../../../../../shared/offer-domain/counter/CounterNegotiationCard';
import {
  formatDateTime,
  formatOperationalTolerance,
  hasOperationalToleranceCounter,
  hasProposalValidityCounter,
  isValidOperationalToleranceValue,
  resolveOperationalToleranceOption,
} from '../../../../../../../shared/offer-domain/counter';
import ClauseDetailSection from '../components/ClauseDetailSection';
import IcRespondAnchor from '../components/IcRespondAnchor';
import ClauseCard from '../../../../../../../shared/offer-domain/clause';
import PaymentComplianceView from '../components/PaymentComplianceView';
import { PortRestrictionsTable } from '../../../../../../../Components/PortRestriction';
import { getProcurementProductNameId } from '../../../../../../../shared/offer-domain/constants';
import {
  displayValue,
  formatProposalDeadline,
} from '../../../../../utils/tenderEvaluationHelpers';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  needsIcReplyOnEvaluatorThread,
  getLastOwnEditableResponse,
  isLastResponseEvaluator,
  needsTeAgreeOnLastCounter,
  hasTeClarificationRespondReady,
  toToleranceValidityHistoryItems,
  TYPE_RESPONSE_TOLERANCE,
  TYPE_RESPONSE_VALIDITY,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  createLockRespondHandler,
} from '../../../../../../../shared/offer-domain/respond';
import useClarificationToleranceValidityResponses from '../../../../../../../shared/offer-domain/respond/useClarificationToleranceValidityResponses';
import useSubmittedBidder from '../../../../../hooks/useSubmittedBidder';

const FIELD_TYPE_MAP = {
  operationalTolerance: TYPE_RESPONSE_TOLERANCE,
  proposalValidityPeriodMax: TYPE_RESPONSE_VALIDITY,
  globalNotes: TYPE_RESPONSE_GLOBAL,
  counterPaymentTerm: TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  counterCompliance: TYPE_RESPONSE_COUNTER_COMPLIANCE,
};

const EVALUATOR_FORM_MAIN = {
  statusOfferingWordingFlag: false,
};

const OfferingTab = ({
  procurement,
  procurementId,
  offerId,
  procurementMitraId: procurementMitraIdProp = null,
  isEvaluatorView = true,
  isRespondClosed: isRespondClosedProp = null,
  highlightArmed = false,
  onPendingRespondIds,
}) => {
  const token = getAuthToken();
  const { items } = useSubmittedBidder(procurementId);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [draftNotes, setDraftNotes] = useState({});

  const matchedBidder = useMemo(
    () =>
      items.find(
        item =>
          String(item.idOffer) === String(offerId) ||
          String(item.id) === String(offerId) ||
          (procurementMitraIdProp &&
            String(item.id) === String(procurementMitraIdProp))
      ) ?? null,
    [items, offerId, procurementMitraIdProp]
  );

  const bidder = useMemo(() => {
    if (procurementMitraIdProp) {
      return {
        ...matchedBidder,
        id: procurementMitraIdProp,
        idOffer: offerId ?? matchedBidder?.idOffer,
      };
    }
    return matchedBidder;
  }, [matchedBidder, offerId, procurementMitraIdProp]);

  useEffect(() => {
    setExpandedSections({});
    setEditingField(null);
    setDraftNotes({});
  }, [offerId, bidder?.id]);

  const isRespondClosed = Boolean(isRespondClosedProp);

  const {
    responses,
    isLoading: isResponsesLoading,
    isSaving,
    saveResponse,
    deleteResponse,
    lockResponse,
  } = useClarificationToleranceValidityResponses(
    token,
    procurementId,
    bidder?.id,
    isEvaluatorView
  );

  const handleLockRespond = createLockRespondHandler(lockResponse);

  const productName = procurement?.productType?.product?.name;
  const productType = procurement?.productType?.description;
  const productSpecification = procurement?.productSpecification;

  const hasToleranceCounter = hasOperationalToleranceCounter(
    procurement?.operationalToleranceCounter,
    procurement?.operationalToleranceOptionsCounter,
    procurement?.operationalToleranceOptionsCounterLabel
  );
  const hasValidityCounter = hasProposalValidityCounter(
    procurement?.proposalValidityPeriodMaxCounter
  );

  const toleranceResponses = useMemo(
    () => responses[TYPE_RESPONSE_TOLERANCE] ?? [],
    [responses]
  );
  const validityResponses = useMemo(
    () => responses[TYPE_RESPONSE_VALIDITY] ?? [],
    [responses]
  );

  const toleranceOwnResponse = useMemo(
    () =>
      getOwnClauseResponse(toleranceResponses, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, toleranceResponses]
  );
  const validityOwnResponse = useMemo(
    () =>
      getOwnClauseResponse(validityResponses, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, validityResponses]
  );

  const toleranceHistory = useMemo(
    () => toToleranceValidityHistoryItems(toleranceResponses),
    [toleranceResponses]
  );
  const validityHistory = useMemo(
    () => toToleranceValidityHistoryItems(validityResponses),
    [validityResponses]
  );

  const canReplyTolerance = isEvaluatorView
    ? !isRespondClosed && !isLastResponseEvaluator(toleranceResponses)
    : needsIcReplyOnEvaluatorThread(toleranceResponses);
  const canReplyValidity = isEvaluatorView
    ? !isRespondClosed && !isLastResponseEvaluator(validityResponses)
    : needsIcReplyOnEvaluatorThread(validityResponses);

  const typeClauseResponses = useMemo(
    () => ({
      globalNotes: responses[TYPE_RESPONSE_GLOBAL] ?? [],
      counterPaymentTerm: responses[TYPE_RESPONSE_COUNTER_PAYMENT_TERM] ?? [],
      counterCompliance: responses[TYPE_RESPONSE_COUNTER_COMPLIANCE] ?? [],
    }),
    [responses]
  );

  const [clausePendingIds, setClausePendingIds] = useState(null);
  const [clauseHasClarificationRespond, setClauseHasClarificationRespond] =
    useState(false);

  const handleClausePending = (ids, meta = {}) => {
    setClausePendingIds(ids);
    if (typeof meta.hasClarificationRespond === 'boolean') {
      setClauseHasClarificationRespond(meta.hasClarificationRespond);
    }
  };

  const livePendingIds = useMemo(() => {
    const isPending = isEvaluatorView
      ? needsTeAgreeOnLastCounter
      : needsIcReplyOnEvaluatorThread;
    const ids = [];
    if (isPending(validityResponses)) {
      ids.push('proposalValidityPeriodMax');
    }
    if (isPending(toleranceResponses)) {
      ids.push('operationalTolerance');
    }
    if (isPending(typeClauseResponses.globalNotes)) {
      ids.push('globalNotes');
    }
    if (isPending(typeClauseResponses.counterPaymentTerm)) {
      ids.push('counterPaymentTerm');
    }
    if (isPending(typeClauseResponses.counterCompliance)) {
      ids.push('counterCompliance');
    }
    return [...ids, ...(clausePendingIds ?? [])];
  }, [
    isEvaluatorView,
    validityResponses,
    toleranceResponses,
    typeClauseResponses,
    clausePendingIds,
  ]);

  const hasClarificationRespond = useMemo(() => {
    if (!isEvaluatorView) return true;
    const lists = [
      validityResponses,
      toleranceResponses,
      typeClauseResponses.globalNotes,
      typeClauseResponses.counterPaymentTerm,
      typeClauseResponses.counterCompliance,
    ];
    return (
      lists.some(hasTeClarificationRespondReady) ||
      clauseHasClarificationRespond
    );
  }, [
    isEvaluatorView,
    validityResponses,
    toleranceResponses,
    typeClauseResponses,
    clauseHasClarificationRespond,
  ]);

  const highlightIds = useMemo(
    () => (highlightArmed ? livePendingIds : []),
    [highlightArmed, livePendingIds]
  );

  useEffect(() => {
    if (!onPendingRespondIds || isResponsesLoading || clausePendingIds === null)
      return;
    onPendingRespondIds(
      livePendingIds.map(id => ({
        id,
        tab: 'offering',
      })),
      { hasClarificationRespond }
    );
  }, [
    livePendingIds,
    onPendingRespondIds,
    isResponsesLoading,
    clausePendingIds,
    hasClarificationRespond,
  ]);

  const handleTypeDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const handleTypeSave = async clauseId => {
    const typeResponse = FIELD_TYPE_MAP[clauseId];
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!hasClauseResponse(respondNotes)) {
      toast.error('Respond notes cannot be empty');
      return;
    }
    try {
      const saved = await saveResponse(typeResponse, respondNotes);
      if (!saved) {
        toast.error('Failed to save response');
        return;
      }
      setDraftNotes(prev => ({
        ...prev,
        [clauseId]: saved?.respondNotes ?? respondNotes,
      }));
      setExpandedSections(prev => ({ ...prev, [clauseId]: true }));
      toast.success('Response saved successfully');
    } catch {
      toast.error('Failed to save response');
    }
  };

  const handleTypeSecondary = async (clauseId, hasSaved) => {
    if (hasSaved) {
      try {
        await deleteResponse(FIELD_TYPE_MAP[clauseId]);
        setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
        toast.success('Response removed successfully');
      } catch {
        toast.error('Failed to remove response');
      }
      return;
    }
    const own = getOwnClauseResponse(typeClauseResponses[clauseId], {
      isEvaluator: isEvaluatorView,
    });
    setDraftNotes(prev => ({ ...prev, [clauseId]: own?.respondNotes ?? '' }));
  };

  const renderTypeClauseCard = (
    key,
    title,
    description,
    { canInitiate = false, isRespondClosed: cardClosed = false } = {}
  ) => (
    <Row>
      <ClauseCard
        clause={{
          id: key,
          clauseTitle: title,
          clauseDescription: description,
        }}
        clauseResponses={typeClauseResponses}
        draftNotes={draftNotes}
        expandedClauses={expandedSections}
        formMain={EVALUATOR_FORM_MAIN}
        isClauseResponsesSaving={isSaving}
        isClauseResponsesLoading={false}
        isEvaluatorView={isEvaluatorView}
        handleDraftChange={handleTypeDraftChange}
        handleClauseSecondaryAction={handleTypeSecondary}
        handleClauseSave={handleTypeSave}
        toggleClauseExpand={toggleSectionExpand}
        onLockResponse={isEvaluatorView ? handleLockRespond : undefined}
        canInitiate={canInitiate}
        isRespondClosed={cardClosed}
        anchorId={`ic-respond-${key}`}
        highlightPending={highlightIds.includes(key)}
      />
    </Row>
  );

  const toggleSectionExpand = field => {
    setExpandedSections(prev => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;

      if (hasToleranceCounter && next.operationalTolerance === undefined) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (hasValidityCounter && next.proposalValidityPeriodMax === undefined) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }
      if (
        hasClauseResponse(toleranceOwnResponse?.respondNotes) &&
        !next.operationalTolerance
      ) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (
        hasClauseResponse(validityOwnResponse?.respondNotes) &&
        !next.proposalValidityPeriodMax
      ) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    hasToleranceCounter,
    hasValidityCounter,
    toleranceOwnResponse?.respondNotes,
    validityOwnResponse?.respondNotes,
  ]);

  useEffect(() => {
    if (!highlightIds.length) return;
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;
      highlightIds.forEach(id => {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [highlightIds]);

  useEffect(() => {
    setDraftNotes(prev => {
      const next = { ...prev };

      if (editingField !== 'operationalTolerance') {
        next.operationalTolerance = isEvaluatorView
          ? (toleranceOwnResponse?.respondNotes ?? '')
          : '';
      }
      if (editingField !== 'proposalValidityPeriodMax') {
        next.proposalValidityPeriodMax = isEvaluatorView
          ? (validityOwnResponse?.respondNotes ?? '')
          : '';
      }

      return next;
    });
  }, [
    editingField,
    isEvaluatorView,
    toleranceOwnResponse?.respondNotes,
    validityOwnResponse?.respondNotes,
  ]);

  const toleranceOption = resolveOperationalToleranceOption({
    label: procurement?.operationalToleranceOptionsCounterLabel,
    optionsCounter: procurement?.operationalToleranceOptionsCounter,
  });

  const validityLastEditable = getLastOwnEditableResponse(validityResponses, {
    isEvaluator: isEvaluatorView,
  });
  const toleranceLastEditable = getLastOwnEditableResponse(toleranceResponses, {
    isEvaluator: isEvaluatorView,
  });

  const startRespond = field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const lastEditable =
      typeResponse === TYPE_RESPONSE_TOLERANCE
        ? toleranceLastEditable
        : validityLastEditable;

    setDraftNotes(prev => ({
      ...prev,
      [field]: lastEditable?.respondNotes ?? '',
    }));
    setExpandedSections(prev => ({ ...prev, [field]: true }));
    setEditingField(field);
  };

  const cancelRespond = field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const own =
      typeResponse === TYPE_RESPONSE_TOLERANCE
        ? toleranceOwnResponse
        : validityOwnResponse;

    setDraftNotes(prev => ({
      ...prev,
      [field]: own?.respondNotes ?? '',
    }));
    setEditingField(null);
  };

  const handleSaveRespond = async field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const respondNotes = String(draftNotes[field] ?? '').trim();

    if (!hasClauseResponse(respondNotes)) {
      toast.error('Respond notes cannot be empty');
      return;
    }

    try {
      const saved = await saveResponse(typeResponse, respondNotes);
      if (!saved) {
        toast.error('Failed to save response');
        return;
      }
      setDraftNotes(prev => ({
        ...prev,
        [field]: saved?.respondNotes ?? respondNotes,
      }));
      setEditingField(null);
      setExpandedSections(prev => ({ ...prev, [field]: true }));
      toast.success('Response saved successfully');
    } catch {
      toast.error('Failed to save response');
    }
  };

  const handleDeleteRespond = async field => {
    const typeResponse = FIELD_TYPE_MAP[field];

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This response note will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f1556c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      await deleteResponse(typeResponse);
      setDraftNotes(prev => ({ ...prev, [field]: '' }));
      setEditingField(null);
      toast.success('Response removed successfully');
    } catch {
      toast.error('Failed to remove response');
    }
  };

  const canRespondTolerance =
    canReplyTolerance && editingField !== 'operationalTolerance';

  const canRespondValidity =
    canReplyValidity && editingField !== 'proposalValidityPeriodMax';

  return (
    <>
      <SectionCard>
        <SectionHeader title='Product Specification/ Quantity' />
        <Row className='gy-4'>
          <Col lg={4}>
            <DetailItem
              label='Product Name'
              value={displayValue(productName)}
            />
          </Col>
          <Col lg={4}>
            <DetailItem
              label='Product Type'
              value={displayValue(productType)}
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Product Specification'
              value={productSpecification}
              isHtml
            />
          </Col>
        </Row>
      </SectionCard>

      <Row className='mb-4'>
        <Col sm={12}>
          <IcRespondAnchor
            id='proposalValidityPeriodMax'
            pendingIds={highlightIds}
          >
            <CounterNegotiationCard
              title='Validity Period'
              subtitle='Review validity period requirements'
              label='Valid To (Jakarta Local Time)'
              currentDisplay={formatDateTime(
                procurement?.proposalValidityPeriodMax
              )}
              extraFields={[
                {
                  label: 'Submission Deadline (Jakarta Local Time)',
                  value: formatProposalDeadline(
                    procurement?.proposalSubmissionDeadline
                  ),
                },
              ]}
              counterDisplay={
                hasValidityCounter
                  ? formatDateTime(
                      procurement?.proposalValidityPeriodMaxCounter
                    )
                  : null
              }
              hasCounter={hasValidityCounter}
              historyItems={validityHistory}
              isExpanded={Boolean(expandedSections.proposalValidityPeriodMax)}
              onToggleExpand={() =>
                toggleSectionExpand('proposalValidityPeriodMax')
              }
              editable={false}
              isEvaluatorView={isEvaluatorView}
              isEditing={editingField === 'proposalValidityPeriodMax'}
              isSaving={isSaving}
              isDraftDirty={
                isEvaluatorView
                  ? String(draftNotes.proposalValidityPeriodMax ?? '') !==
                    String(validityOwnResponse?.respondNotes ?? '')
                  : String(
                      draftNotes.proposalValidityPeriodMax ?? ''
                    ).trim() !== ''
              }
              onRespond={
                canRespondValidity
                  ? () => startRespond('proposalValidityPeriodMax')
                  : undefined
              }
              onStartEdit={
                !isRespondClosed && validityLastEditable
                  ? () => startRespond('proposalValidityPeriodMax')
                  : undefined
              }
              onCancel={() => cancelRespond('proposalValidityPeriodMax')}
              onSave={() => handleSaveRespond('proposalValidityPeriodMax')}
              onLockResponse={
                isEvaluatorView && !isRespondClosed
                  ? handleLockRespond
                  : undefined
              }
              onDelete={
                !isRespondClosed && validityLastEditable
                  ? () => handleDeleteRespond('proposalValidityPeriodMax')
                  : undefined
              }
              isRespondClosed={isRespondClosed}
            >
              <RespondNotesEditor
                id='respond-proposal-validity'
                value={draftNotes.proposalValidityPeriodMax ?? ''}
                isDraftDirty={
                  isEvaluatorView
                    ? String(draftNotes.proposalValidityPeriodMax ?? '') !==
                      String(validityOwnResponse?.respondNotes ?? '')
                    : String(
                        draftNotes.proposalValidityPeriodMax ?? ''
                      ).trim() !== ''
                }
                onChange={e =>
                  setDraftNotes(prev => ({
                    ...prev,
                    proposalValidityPeriodMax: e.target.value,
                  }))
                }
              />
            </CounterNegotiationCard>
          </IcRespondAnchor>
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col sm={12}>
          <IcRespondAnchor id='operationalTolerance' pendingIds={highlightIds}>
            <CounterNegotiationCard
              title='Operational Tolerance'
              subtitle='Review operational tolerance requirements'
              partyType={procurement?.partyType}
              label='Operational Tolerance'
              currentDisplay={formatOperationalTolerance(
                procurement?.operationalTolerance
              )}
              counterDisplay={
                isValidOperationalToleranceValue(
                  procurement?.operationalToleranceCounter
                )
                  ? formatOperationalTolerance(
                      procurement?.operationalToleranceCounter
                    )
                  : null
              }
              partyTypeCounter={toleranceOption || null}
              hasCounter={hasToleranceCounter}
              historyItems={toleranceHistory}
              isExpanded={Boolean(expandedSections.operationalTolerance)}
              onToggleExpand={() => toggleSectionExpand('operationalTolerance')}
              editable={false}
              isEvaluatorView={isEvaluatorView}
              isEditing={editingField === 'operationalTolerance'}
              isSaving={isSaving}
              isDraftDirty={
                isEvaluatorView
                  ? String(draftNotes.operationalTolerance ?? '') !==
                    String(toleranceOwnResponse?.respondNotes ?? '')
                  : String(draftNotes.operationalTolerance ?? '').trim() !== ''
              }
              onRespond={
                canRespondTolerance
                  ? () => startRespond('operationalTolerance')
                  : undefined
              }
              onStartEdit={
                !isRespondClosed && toleranceLastEditable
                  ? () => startRespond('operationalTolerance')
                  : undefined
              }
              onCancel={() => cancelRespond('operationalTolerance')}
              onSave={() => handleSaveRespond('operationalTolerance')}
              onLockResponse={
                isEvaluatorView && !isRespondClosed
                  ? handleLockRespond
                  : undefined
              }
              onDelete={
                !isRespondClosed && toleranceLastEditable
                  ? () => handleDeleteRespond('operationalTolerance')
                  : undefined
              }
              isRespondClosed={isRespondClosed}
            >
              <RespondNotesEditor
                id='respond-operational-tolerance'
                value={draftNotes.operationalTolerance ?? ''}
                isDraftDirty={
                  isEvaluatorView
                    ? String(draftNotes.operationalTolerance ?? '') !==
                      String(toleranceOwnResponse?.respondNotes ?? '')
                    : String(draftNotes.operationalTolerance ?? '').trim() !==
                      ''
                }
                onChange={e =>
                  setDraftNotes(prev => ({
                    ...prev,
                    operationalTolerance: e.target.value,
                  }))
                }
              />
            </CounterNegotiationCard>
          </IcRespondAnchor>
        </Col>
      </Row>

      <SectionCard>
        {renderTypeClauseCard(
          'globalNotes',
          'Notes',
          procurement?.globalNotes,
          { canInitiate: true, isRespondClosed }
        )}
        <hr className='my-4 border-secondary border-dashed' />
        <div className='mt-2'>
          <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
          <PortRestrictionsTable
            ports={procurement?.portRestriction}
            productId={getProcurementProductNameId(procurement)}
          />
        </div>
      </SectionCard>

      <ClauseDetailSection
        procurement={procurement}
        procurementId={procurementId}
        offerId={offerId}
        procurementMitraId={procurementMitraIdProp}
        isEvaluatorView={isEvaluatorView}
        isRespondClosed={isRespondClosed}
        highlightArmed={highlightArmed}
        onPendingRespondIds={handleClausePending}
      />

      <PaymentComplianceView
        procurement={procurement}
        procurementMitraId={bidder?.id}
        paymentRespond={renderTypeClauseCard(
          'counterPaymentTerm',
          'Payment Terms Clarification',
          '',
          { canInitiate: isEvaluatorView, isRespondClosed }
        )}
        complianceRespond={renderTypeClauseCard(
          'counterCompliance',
          'Compliance Clarification',
          '',
          { canInitiate: isEvaluatorView, isRespondClosed }
        )}
      />
    </>
  );
};

export default OfferingTab;
